/**
 * Unified take-profit leg: price + allocation config in one object.
 * @typedef {{ price: number, percentage: number, rMultiple: number }} TakeProfitLeg
 */

import { evenSplitTakeProfitPercents } from './bracketTpQuantities';

/** @param {TakeProfitLeg[]} legs */
export function pricesFromLegs(legs) {
  if (!Array.isArray(legs)) return [];
  return legs
    .map((leg) => Number(leg?.price))
    .filter((p) => Number.isFinite(p));
}

/** @param {TakeProfitLeg[]} legs */
export function configFromLegs(legs) {
  if (!Array.isArray(legs)) return [];
  return legs.map((leg) => ({
    percentage: leg?.percentage ?? 0,
    rMultiple: leg?.rMultiple ?? 1,
  }));
}

/**
 * Build TakeProfitLeg[] from parallel price + config arrays.
 * @param {number[]} prices
 * @param {Array<{ percentage?: number, rMultiple?: number }>} [configLegs]
 * @param {Array<{ percentage?: number, rMultiple?: number }>} [templateLegs]
 * @returns {TakeProfitLeg[]}
 */
export function legsFromPricesAndConfig(prices, configLegs = [], templateLegs = []) {
  if (!Array.isArray(prices) || prices.length === 0) return [];
  const cfg = (Array.isArray(configLegs) && configLegs.length > 0)
    ? configLegs
    : (Array.isArray(templateLegs) ? templateLegs : []);
  const even = evenSplitTakeProfitPercents(prices.length);
  return prices.map((rawPrice, idx) => {
    const price = Number(rawPrice);
    const fromCfg = cfg[idx];
    const fallback = even[idx] || { percentage: 0, rMultiple: idx + 1 };
    return {
      price,
      percentage: fromCfg?.percentage ?? fallback.percentage,
      rMultiple: fromCfg?.rMultiple ?? fallback.rMultiple,
    };
  });
}

/**
 * Update prices on existing legs, preserving percentage/rMultiple where possible.
 * @param {TakeProfitLeg[]} legs
 * @param {number[]} prices
 * @returns {TakeProfitLeg[]}
 */
export function updateLegPrices(legs, prices) {
  if (!Array.isArray(prices)) return [];
  const prev = Array.isArray(legs) ? legs : [];
  const even = evenSplitTakeProfitPercents(prices.length);
  return prices.map((rawPrice, idx) => {
    const price = Number(rawPrice);
    const prevLeg = prev[idx];
    const fallback = even[idx] || { percentage: 0, rMultiple: idx + 1 };
    return {
      price,
      percentage: prevLeg?.percentage ?? fallback.percentage,
      rMultiple: prevLeg?.rMultiple ?? fallback.rMultiple,
    };
  });
}

/** @param {TakeProfitLeg[]} legs */
export function sortLegsByPrice(legs) {
  if (!Array.isArray(legs)) return [];
  return [...legs].sort((a, b) => a.price - b.price);
}

/**
 * Redistribute integer percentages across legs to sum to exactly 100.
 * Weights are proportional to each leg's current percentage; falls back to
 * evenSplitTakeProfitPercents when all weights are zero.
 *
 * @param {TakeProfitLeg[]} legs
 * @returns {TakeProfitLeg[]}
 */
export function rebalanceTakeProfitPercentages(legs) {
  if (!Array.isArray(legs) || legs.length === 0) return [];
  if (legs.length === 1) {
    return [{ ...legs[0], percentage: 100 }];
  }

  const weights = legs.map((leg) => Math.max(0, Number(leg.percentage) || 0));
  const totalWeight = weights.reduce((s, w) => s + w, 0);

  if (totalWeight <= 0) {
    const even = evenSplitTakeProfitPercents(legs.length);
    return legs.map((leg, i) => ({
      ...leg,
      percentage: even[i].percentage,
    }));
  }

  const basePcts = weights.map((w) => Math.floor((w / totalWeight) * 100));
  let remainder = 100 - basePcts.reduce((s, p) => s + p, 0);
  const pcts = [...basePcts];

  if (remainder > 0) {
    const fractionalParts = weights.map((w, index) => ({
      index,
      fractionalPart: (w / totalWeight) * 100 - basePcts[index],
    }));
    fractionalParts.sort((a, b) => {
      if (Math.abs(a.fractionalPart - b.fractionalPart) > 0.0001) {
        return b.fractionalPart - a.fractionalPart;
      }
      return a.index - b.index;
    });
    for (let i = 0; i < remainder && i < fractionalParts.length; i++) {
      pcts[fractionalParts[i].index]++;
    }
  }

  return legs.map((leg, i) => ({ ...leg, percentage: pcts[i] }));
}

/**
 * Remove one TP leg and rebalance remaining percentages to 100%.
 *
 * @param {TakeProfitLeg[]} legs
 * @param {number} removeIndex
 * @returns {TakeProfitLeg[]}
 */
export function removeTakeProfitLegAndRebalance(legs, removeIndex) {
  if (!Array.isArray(legs) || !Number.isFinite(removeIndex)) return [];
  const remaining = legs
    .filter((_, idx) => idx !== removeIndex)
    .map((leg) => ({ ...leg }));
  return rebalanceTakeProfitPercentages(remaining);
}

/**
 * @param {object} positionBracketMode
 * @returns {number[]}
 */
export function positionBracketTakeProfitPrices(positionBracketMode) {
  if (!positionBracketMode) return [];
  if (Array.isArray(positionBracketMode.takeProfits) && positionBracketMode.takeProfits.length > 0) {
    return pricesFromLegs(positionBracketMode.takeProfits);
  }
  return Array.isArray(positionBracketMode.takeProfitPrices)
    ? positionBracketMode.takeProfitPrices.map(Number).filter((n) => Number.isFinite(n))
    : [];
}

/**
 * Resolve TP config rows for bracket panel display and metrics.
 * Precedence: live order → position draft legs → entry draft legs →
 * explicit order overrides (including empty after chart/panel removal) → template.
 *
 * @param {object} params
 * @param {{ takeProfits?: Array<{ rMultiple?: number, percentage?: number, price?: number }> }|null} [params.bracketOrderData]
 * @param {boolean} [params.isPositionBracketPending]
 * @param {object} [params.positionBracketMode]
 * @param {TakeProfitLeg[]} [params.entryTakeProfits]
 * @param {{ takeProfits?: Array<{ rMultiple?: number, percentage?: number }> }|null} [params.orderBracketOverrides]
 * @param {{ takeProfits?: Array<{ rMultiple?: number, percentage?: number }> }|null} [params.selectedTemplate]
 * @returns {Array<{ rMultiple?: number, percentage?: number }>}
 */
export function resolveEffectiveTakeProfitConfig({
  bracketOrderData,
  isPositionBracketPending = false,
  positionBracketMode,
  entryTakeProfits = [],
  orderBracketOverrides,
  selectedTemplate,
}) {
  if (bracketOrderData?.takeProfits?.length > 0) {
    return bracketOrderData.takeProfits.map((tp) => ({
      rMultiple: tp.rMultiple,
      percentage: tp.percentage,
    }));
  }

  if (isPositionBracketPending) {
    const legs = positionBracketTakeProfits(positionBracketMode);
    if (legs.length > 0) {
      return legs.map(({ rMultiple, percentage }) => ({ rMultiple, percentage }));
    }
    return [];
  }

  if (Array.isArray(entryTakeProfits) && entryTakeProfits.length > 0) {
    return entryTakeProfits.map(({ rMultiple, percentage }) => ({ rMultiple, percentage }));
  }

  if (orderBracketOverrides && Array.isArray(orderBracketOverrides.takeProfits)) {
    return orderBracketOverrides.takeProfits;
  }

  return selectedTemplate?.takeProfits || [];
}

/**
 * @param {object} positionBracketMode
 * @returns {TakeProfitLeg[]}
 */
export function positionBracketTakeProfits(positionBracketMode) {
  if (!positionBracketMode) return [];
  if (Array.isArray(positionBracketMode.takeProfits) && positionBracketMode.takeProfits.length > 0) {
    return positionBracketMode.takeProfits.map((leg) => ({ ...leg }));
  }
  const prices = positionBracketTakeProfitPrices(positionBracketMode);
  return legsFromPricesAndConfig(prices, [], []);
}
